import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import POSState from 'features/POSRegisters/interfaces/POSState';

// region createDeviceIdsFromNodes
const createDeviceIdsFromNodes = (state: POSState) => {
  const {registers} = state;

  const cloudServiceNodes = `${DeviceType.CLOUD_SERVICES}-${1}`;
  const networkCircuitNodes = `${DeviceType.NETWORK_CIRCUIT}-${1}`;
  const networkSwitchNodes = `${DeviceType.NETWORK_SWITCH}-${1}`;
  const registerNodes = registers.map((device, index) => `${DeviceType.REGISTER}-${index + 1}`);

  return {
    networkNodeGridAreaIds: [cloudServiceNodes, networkCircuitNodes, networkSwitchNodes],
    registerNodeGridAreaIds: [...registerNodes],
  };
};
// endregion createDeviceIdsFromNodes

// region breakRegisterNodesIntoRows
const breakRegisterNodesIntoRows = (registerNodeGridAreaIds: string[], rowLength: number) => {
  const chunkArray = (registerNodeGridAreaIds: string[], rowLength: number) => {
    const arrayToChunks = registerNodeGridAreaIds.reduce((acc: string[][], curr, index) => {
      const chunkIndex = Math.floor(index / rowLength);
      if (!acc[chunkIndex]) {
        acc[chunkIndex] = [];
      }
      acc[chunkIndex].push(curr);
      return acc;
    }, []);

    return arrayToChunks;
  };

  if (registerNodeGridAreaIds.length > rowLength) {
    // break into rows based on rowLength
    return chunkArray(registerNodeGridAreaIds, rowLength);
  } else if (registerNodeGridAreaIds.length === rowLength) {
    return [registerNodeGridAreaIds];
  } else if (registerNodeGridAreaIds.length % 3 !== 0) {
    // if less than rowLength break into rows of 3 to center devices with networkDevices
    return chunkArray(registerNodeGridAreaIds, 3);
  } else {
    return [registerNodeGridAreaIds];
  }
};
// endregion breakRegisterNodesIntoRows

// region insertBlanksToCenterDevices
const insertBlanksToCenterDevices = (
  networkNodeGridAreaIds: string[],
  registersBrokenIntoRows: string[][]
) => {
  let networkNodeWithBlankFillers: string[] = [...networkNodeGridAreaIds];
  let registerNodeWithBlankFillers: string[][] = [...registersBrokenIntoRows];

  const networkNodeSize = networkNodeGridAreaIds.length;
  const registerNodeFirstRowSize = registersBrokenIntoRows[0].length;

  if (networkNodeSize !== registerNodeFirstRowSize) {
    const fillerArrayAmount = Math.abs(networkNodeSize - registerNodeFirstRowSize);

    if (networkNodeSize < registerNodeFirstRowSize) {
      // insert blank fillers into network nodes
      const blankFillers = Array.from(
        {length: fillerArrayAmount},
        (_, index) => `network-blank-${index + 1}`
      );

      blankFillers.forEach((blankNode, index) => {
        // alternate between first and end of array to center network devices
        if (index % 2 === 0) {
          networkNodeWithBlankFillers.push(blankNode);
        } else {
          networkNodeWithBlankFillers.splice(0, 0, blankNode);
        }
      });
    }
  }

  // insert blank fillers into each register row
  const networkNodeSizeWithFillers = networkNodeWithBlankFillers.length;
  registerNodeWithBlankFillers.forEach((registerRow, registerNodeIndex) => {
    const registerRowSize = registerRow.length;
    if (registerRowSize < networkNodeSizeWithFillers) {
      const fillerArrayAmount = networkNodeSizeWithFillers - registerRowSize;

      const registerBlankFillers = Array.from(
        {length: fillerArrayAmount},
        (_, index) => `register-blank-${registerNodeIndex}-${index + 1}`
      );
      registerNodeWithBlankFillers[registerNodeIndex] = [...registerRow, ...registerBlankFillers];
    }
  });

  //create pathway nodes
  const pathwayGridAreaIds = Array.from({length: networkNodeSizeWithFillers}, () => 'pathway');

  return {
    pathwayGridAreaIds,
    networkNodeWithBlankFillers,
    registerNodeWithBlankFillers,
  };
};
// endregion insertBlanksToCenterDevices

// region generateCSSGridAreas
interface GenerateCSSGridAreas {
  networkNodeWithBlankFillers: string[];
  pathwayGridAreaIds: string[];
  registerNodeWithBlankFillers: string[][];
}

const generateCSSGridAreas = ({
  networkNodeWithBlankFillers,
  pathwayGridAreaIds,
  registerNodeWithBlankFillers,
}: GenerateCSSGridAreas): string => {
  let cssGridTemplateAreas = '';

  cssGridTemplateAreas += `'${networkNodeWithBlankFillers.join(' ')}'`;
  cssGridTemplateAreas += `'${pathwayGridAreaIds.join(' ')}'`;

  registerNodeWithBlankFillers.forEach(item => {
    cssGridTemplateAreas += `'${item.join(' ')}'`;
  });

  return cssGridTemplateAreas;
};
// endregion generateCSSGridAreas

interface GenerateNodeGridAreaIds {
  cssGridTemplateAreas: string;
  totalColumns: number;
}

export default function generateNodeGridAreaIds({
  state,
  columns,
}: {
  columns: number;
  state: POSState;
}): GenerateNodeGridAreaIds {
  const {networkNodeGridAreaIds, registerNodeGridAreaIds} = createDeviceIdsFromNodes(state);

  const registersBrokenIntoRows = breakRegisterNodesIntoRows(registerNodeGridAreaIds, columns);

  const totalColumns: number = registersBrokenIntoRows[0].length;

  const {
    networkNodeWithBlankFillers,
    registerNodeWithBlankFillers,
    pathwayGridAreaIds,
  } = insertBlanksToCenterDevices(networkNodeGridAreaIds, registersBrokenIntoRows);

  const cssGridTemplateAreas = generateCSSGridAreas({
    networkNodeWithBlankFillers,
    pathwayGridAreaIds,
    registerNodeWithBlankFillers,
  });

  return {
    cssGridTemplateAreas,
    totalColumns,
  };
}
